import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  LinearProgress,
  Autocomplete
} from '@mui/material';
import { 
  Person as PersonIcon,
  Photo as PhotoIcon,
  Tag as TagIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { adminApi } from '../../redux/adminApiSlice';
import ProfilePictureAvatar from './ProfilePictureAvatar';

interface ProfilePicture {
  filename: string;
  type: 'man' | 'woman' | 'manPublic' | 'womanPublic' | 'public';
  gender?: 'male' | 'female';
  isPublic: boolean;
}

interface MockUserData {
  displayName: string;
  username: string;
  email: string;
  profilePicture: string;
  publicPicture: string;
  gender: string;
  race: string;
  sexualOrientation: string;
  age: number;
  tags: string[];
  homeNeighborhood: string;
  bio: string;
}

interface Neighborhood {
  _id: string;
  name: string;
  coordinates: {
    type: string;
    coordinates: [number, number];
  };
}

const BIO_TEMPLATES = [
  "Love {tag1} and spending time exploring {tag2}. Always up for new adventures!",
  "Passionate about {tag1} and {tag2}. Looking to connect with like-minded people in the neighborhood.",
  "{tag1} enthusiast who also enjoys {tag2}. Let's grab coffee and chat!",
  "Life is better with {tag1} and {tag2}. Always down to discover new spots around the city.",
  "Big fan of {tag1} and {tag2}. Love meeting new people and sharing experiences!"
];

const FIRST_NAMES = {
  male: ['Alex', 'James', 'Michael', 'David', 'Chris', 'Matt', 'Ryan', 'Josh', 'Nick', 'Sam', 'Jake', 'Ben', 'Luke', 'Noah', 'Ethan', 'Mason', 'Logan', 'Jacob', 'William', 'Oliver'],
  female: ['Emma', 'Olivia', 'Ava', 'Isabella', 'Sophia', 'Mia', 'Charlotte', 'Amelia', 'Harper', 'Evelyn', 'Abigail', 'Emily', 'Ella', 'Elizabeth', 'Camila', 'Luna', 'Sofia', 'Avery', 'Mila', 'Aria']
};

const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin'];

const MockUserCreator: React.FC = () => {
  const [profilePictures, setProfilePictures] = useState<ProfilePicture[]>([]);
  const [usedPictures, setUsedPictures] = useState<Set<string>>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('mockUserCreator_usedPictures');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [currentPicture, setCurrentPicture] = useState<ProfilePicture | null>(null);
  const [publicPictures, setPublicPictures] = useState<ProfilePicture[]>([]);
  const [availablePublicPictures, setAvailablePublicPictures] = useState<ProfilePicture[]>(() => {
    // Initialize from localStorage
    return []; // Will be properly set in initializeProfilePictures
  });
  
  const [userData, setUserData] = useState<MockUserData>({
    displayName: '',
    username: '',
    email: '',
    profilePicture: '',
    publicPicture: '',
    gender: '',
    race: '',
    sexualOrientation: '',
    age: 18,
    tags: [],
    homeNeighborhood: '',
    bio: ''
  });
  
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagCount, setTagCount] = useState(15);
  const [selectedBioTemplate, setSelectedBioTemplate] = useState(0);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState(0);

  // API hooks
  const { data: identityOptions } = adminApi.useGetIdentityOptionsQuery();
  const { data: availableTags = [] } = adminApi.useGetAvailableTagsQuery();
  const { data: nycNeighborhoods = [] } = adminApi.useGetNYCNeighborhoodsQuery();
  const [createMockUser] = adminApi.useCreateMockUserMutation();

  // Use refs to break circular dependency
  const availablePublicPicturesRef = useRef<ProfilePicture[]>([]);
  const neighborhoodsRef = useRef<Neighborhood[]>([]);
  
  // Update refs when state changes
  useEffect(() => {
    availablePublicPicturesRef.current = availablePublicPictures;
  }, [availablePublicPictures]);
  
  useEffect(() => {
    neighborhoodsRef.current = neighborhoods;
  }, [neighborhoods]);

  const generateUserData = useCallback((picture: ProfilePicture) => {
    // Generate random name based on gender
    const isGendered = picture.type === 'man' || picture.type === 'woman' || picture.type === 'manPublic' || picture.type === 'womanPublic';
    const gender = isGendered ? picture.gender : Math.random() > 0.5 ? 'male' : 'female';
    
    const firstNames = gender === 'male' ? FIRST_NAMES.male : FIRST_NAMES.female;
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
    const displayName = `${firstName} ${lastName}`;
    
    // Generate anonymous username (not based on real name)
    const anonymousUsernames = [
      'shadowhunter', 'moonwalker', 'stargazer', 'nightowl', 'dreamcatcher',
      'wanderer', 'explorer', 'adventurer', 'mystic', 'phoenix',
      'rebel', 'maverick', 'ninja', 'wizard', 'knight',
      'ranger', 'scout', 'pilot', 'captain', 'voyager',
      'thunder', 'lightning', 'storm', 'blaze', 'frost',
      'echo', 'ghost', 'spirit', 'soul', 'heart',
      'mind', 'vision', 'dream', 'hope', 'faith',
      'courage', 'brave', 'bold', 'fierce', 'wild',
      'free', 'pure', 'true', 'real', 'honest',
      'bright', 'shine', 'glow', 'spark', 'flame',
      'ocean', 'river', 'mountain', 'forest', 'desert',
      'cosmic', 'stellar', 'lunar', 'solar', 'nova',
      'quantum', 'digital', 'cyber', 'tech', 'code',
      'pixel', 'byte', 'data', 'wave', 'signal',
      'pulse', 'rhythm', 'beat', 'flow', 'vibe',
      'zen', 'calm', 'peace', 'serenity', 'harmony',
      'melody', 'song', 'tune', 'note', 'chord',
      'artist', 'creator', 'maker', 'builder', 'designer',
      'thinker', 'dreamer', 'believer', 'achiever', 'winner'
    ];
    
    const randomBase = anonymousUsernames[Math.floor(Math.random() * anonymousUsernames.length)];
    const randomNumber = Math.floor(Math.random() * 9999) + 1;
    const username = `${randomBase}${randomNumber}`;
    const email = `${username}@mockuser.com`;
    
    // Select random neighborhood from ref
    const currentNeighborhoods = neighborhoodsRef.current;
    const randomNeighborhood = currentNeighborhoods[Math.floor(Math.random() * currentNeighborhoods.length)];
    
    // Assign appropriate public picture based on profile picture type (use ref)
    const currentAvailablePublic = availablePublicPicturesRef.current;
    let publicPicture = '';
    if (picture.type === 'man' || picture.type === 'woman') {
      // For gendered pictures, pair with appropriate public pictures
      const genderAppropriatePublic = currentAvailablePublic.filter(p => {
        if (picture.type === 'man') {
          // Man pictures pair with manPublic or general public (not womanPublic)
          return p.type === 'manPublic' || p.type === 'public';
        } else {
          // Woman pictures pair with womanPublic or general public (not manPublic)
          return p.type === 'womanPublic' || p.type === 'public';
        }
      });
      
      if (genderAppropriatePublic.length > 0) {
        const randomPublic = genderAppropriatePublic[Math.floor(Math.random() * genderAppropriatePublic.length)];
        publicPicture = randomPublic.filename;
      }
    } else if (picture.type === 'manPublic' || picture.type === 'womanPublic') {
      // Public pictures can pair with any general public picture
      const generalPublic = currentAvailablePublic.filter(p => p.type === 'public');
      if (generalPublic.length > 0) {
        const randomPublic = generalPublic[Math.floor(Math.random() * generalPublic.length)];
        publicPicture = randomPublic.filename;
      }
    } else {
      // For general public pictures, pair with any available public picture
      if (currentAvailablePublic.length > 0) {
        const randomPublic = currentAvailablePublic[Math.floor(Math.random() * currentAvailablePublic.length)];
        publicPicture = randomPublic.filename;
      }
    }
    
    // Generate random age between 18-65
    const age = Math.floor(Math.random() * (65 - 18 + 1)) + 18;
    
    setUserData({
      displayName,
      username,
      email,
      profilePicture: picture.filename,
      publicPicture,
      gender: gender === 'male' ? 'male' : 'female',
      race: '',
      sexualOrientation: '',
      age,
      tags: [],
      homeNeighborhood: randomNeighborhood?._id || '',
      bio: ''
    });
    
    // Reset form state
    setSelectedTags([]);
    setSelectedBioTemplate(0);
  }, []); // No dependencies - uses refs instead

  const loadNextPicture = useCallback((allPictures: ProfilePicture[], used: Set<string>) => {
    // First try gendered pictures (man/woman)
    const genderedPictures = allPictures.filter(p => 
      (p.type === 'man' || p.type === 'woman') && !used.has(p.filename)
    );
    
    if (genderedPictures.length > 0) {
      const nextPicture = genderedPictures[0];
      setCurrentPicture(nextPicture);
      generateUserData(nextPicture);
      return;
    }
    
    // Then try public pictures
    const publicPictures = allPictures.filter(p => 
      p.type === 'public' && !used.has(p.filename)
    );
    
    if (publicPictures.length > 0) {
      const nextPicture = publicPictures[0];
      setCurrentPicture(nextPicture);
      generateUserData(nextPicture);
      return;
    }
    
    // No more pictures available
    setCurrentPicture(null);
    // Use a ref or state getter to avoid dependency on createdCount
    setSuccess((prev) => {
      // Access createdCount from closure - this is safe because setSuccess
      // uses the functional update form which doesn't need createdCount in deps
      return `All profile pictures have been used! Created ${createdCount} mock users.`;
    });
  }, [generateUserData, createdCount]);

  // Initialize profile pictures - separate from loading the first picture
  useEffect(() => {
    if (profilePictures.length > 0) return; // Already initialized
    
    // Parse profile pictures from the new-profile-pics folder
    const pictures: ProfilePicture[] = [];
    
    // Add man pictures (man1.jpg to man29.jpg)
    for (let i = 1; i <= 29; i++) {
      pictures.push({
        filename: `man${i}.jpg`,
        type: 'man',
        gender: 'male',
        isPublic: false
      });
    }
    
    // Add woman pictures (woman1.jpg to woman29.jpg)
    for (let i = 1; i <= 29; i++) {
      pictures.push({
        filename: `woman${i}.jpg`,
        type: 'woman',
        gender: 'female',
        isPublic: false
      });
    }
    
    // Add manPublic pictures (manPublic1.jpg to manPublic11.jpg, plus manpublic2.jpg)
    for (let i = 1; i <= 11; i++) {
      pictures.push({
        filename: `manPublic${i}.jpg`,
        type: 'manPublic',
        gender: 'male',
        isPublic: true
      });
    }
    pictures.push({
      filename: 'manpublic2.jpg',
      type: 'manPublic',
      gender: 'male',
      isPublic: true
    });
    
    // Add womanPublic pictures (womanPublic1.jpg to womanPublic5.jpg)
    for (let i = 1; i <= 5; i++) {
      pictures.push({
        filename: `womanPublic${i}.jpg`,
        type: 'womanPublic',
        gender: 'female',
        isPublic: true
      });
    }
    
    // Add public pictures (public3.jpg to public27.jpg)
    for (let i = 3; i <= 27; i++) {
      pictures.push({
        filename: `public${i}.jpg`,
        type: 'public',
        isPublic: true
      });
    }
    
    setProfilePictures(pictures);
    
    // Separate public pictures for pairing
    const publicPics = pictures.filter(p => p.isPublic);
    setPublicPictures(publicPics);
    
    // Restore available public pictures from localStorage
    const storedUsedPublic = localStorage.getItem('mockUserCreator_usedPublicPictures');
    const usedPublicPictures = storedUsedPublic ? new Set(JSON.parse(storedUsedPublic)) : new Set();
    const availablePublic = publicPics.filter(p => !usedPublicPictures.has(p.filename));
    setAvailablePublicPictures(availablePublic);
  }, [profilePictures.length]);

  // Load first picture after initialization and when neighborhoods are ready (only once)
  const hasLoadedFirstPicture = useRef(false);
  useEffect(() => {
    if (!hasLoadedFirstPicture.current && profilePictures.length > 0 && neighborhoods.length > 0 && !currentPicture) {
      hasLoadedFirstPicture.current = true;
      loadNextPicture(profilePictures, usedPictures);
    }
  }, [profilePictures, neighborhoods.length, currentPicture, loadNextPicture, usedPictures]);

  // Update neighborhoods when they load
  useEffect(() => {
    if (nycNeighborhoods.length > 0) {
      setNeighborhoods(nycNeighborhoods);
    }
  }, [nycNeighborhoods]);

  const generateRandomTags = () => {
    const shuffled = [...availableTags].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, tagCount);
    setSelectedTags(selected);
    setUserData(prev => ({ ...prev, tags: selected }));
  };

  const generateBio = () => {
    if (selectedTags.length < 2) return '';
    
    const template = BIO_TEMPLATES[selectedBioTemplate];
    const tag1 = selectedTags[Math.floor(Math.random() * selectedTags.length)];
    let tag2 = selectedTags[Math.floor(Math.random() * selectedTags.length)];
    
    // Ensure tag2 is different from tag1
    while (tag2 === tag1 && selectedTags.length > 1) {
      tag2 = selectedTags[Math.floor(Math.random() * selectedTags.length)];
    }
    
    return template.replace('{tag1}', tag1).replace('{tag2}', tag2);
  };

  const handleCreateUser = async () => {
    if (!currentPicture) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      const bio = generateBio();
      const finalUserData = { ...userData, bio };
      
      const result = await createMockUser(finalUserData).unwrap();
      console.log('[MockUserCreator] User created successfully:', result);
      
      // Mark picture as used
      const newUsedPictures = new Set(usedPictures);
      newUsedPictures.add(currentPicture.filename);
      setUsedPictures(newUsedPictures);
      
      // Persist to localStorage
      localStorage.setItem('mockUserCreator_usedPictures', JSON.stringify(Array.from(newUsedPictures)));
      
      // Remove used public picture from available pool
      if (userData.publicPicture) {
        setAvailablePublicPictures(prev => {
          const updated = prev.filter(p => p.filename !== userData.publicPicture);
          
          // Persist used public pictures to localStorage
          const allPublic = publicPictures.map(p => p.filename);
          const usedPublic = allPublic.filter(filename => !updated.some(p => p.filename === filename));
          localStorage.setItem('mockUserCreator_usedPublicPictures', JSON.stringify(usedPublic));
          
          return updated;
        });
      }
      
      setCreatedCount(prev => prev + 1);
      setSuccess(`Successfully created user: ${userData.displayName}. Check the Users tab to see them in the grid!`);
      
      // Load next picture
      setTimeout(() => {
        loadNextPicture(profilePictures, newUsedPictures);
        setSuccess(null);
      }, 1500);
      
    } catch (err) {
      setError(`Failed to create user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSkipUser = () => {
    if (!currentPicture) return;
    
    // Mark picture as used but don't create user
    const newUsedPictures = new Set(usedPictures);
    newUsedPictures.add(currentPicture.filename);
    setUsedPictures(newUsedPictures);
    
    // Persist to localStorage
    localStorage.setItem('mockUserCreator_usedPictures', JSON.stringify(Array.from(newUsedPictures)));
    
    // Load next picture
    loadNextPicture(profilePictures, newUsedPictures);
  };

  const handleNextPicture = () => {
    if (!currentPicture || profilePictures.length === 0) return;
    
    // Find current picture index
    const currentIndex = profilePictures.findIndex(pic => pic.filename === currentPicture.filename);
    if (currentIndex === -1) return;
    
    // Find next picture (skip used ones if possible, but allow cycling through all)
    let nextIndex = (currentIndex + 1) % profilePictures.length;
    let attempts = 0;
    
    // Try to find an unused picture, but if all are used, just go to next
    while (attempts < profilePictures.length) {
      const nextPicture = profilePictures[nextIndex];
      
      // If we find an unused picture, use it
      if (!usedPictures.has(nextPicture.filename)) {
        setCurrentPicture(nextPicture);
        generateUserData(nextPicture);
        return;
      }
      
      // If this is our second time around and we haven't found unused, just use next picture
      if (attempts >= profilePictures.length - 1) {
        setCurrentPicture(nextPicture);
        generateUserData(nextPicture);
        return;
      }
      
      nextIndex = (nextIndex + 1) % profilePictures.length;
      attempts++;
    }
  };

  if (!currentPicture) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Mock User Creator Complete
        </Typography>
        <Typography variant="body1">
          All profile pictures have been processed. Created {createdCount} mock users.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mock User Creator
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Progress: {usedPictures.size} / {profilePictures.length} pictures processed
        </Typography>
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => {
            localStorage.removeItem('mockUserCreator_usedPictures');
            localStorage.removeItem('mockUserCreator_usedPublicPictures');
            window.location.reload();
          }}
        >
          Reset Progress
        </Button>
      </Box>
      
      <LinearProgress 
        variant="determinate" 
        value={(usedPictures.size / profilePictures.length) * 100} 
        sx={{ mb: 3 }}
      />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Profile Picture Display */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PhotoIcon sx={{ mr: 1 }} />
                Profile Pictures
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <ProfilePictureAvatar
                    filename={currentPicture.filename}
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  />
                  {usedPictures.has(currentPicture.filename) && (
                    <Chip
                      label="USED"
                      color="warning"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  )}
                </Box>
                <Typography variant="subtitle2">
                  Main: {currentPicture.filename}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Type: {currentPicture.type}
                  {currentPicture.gender && ` (${currentPicture.gender})`}
                </Typography>
              </Box>
              
              {userData.publicPicture && (
                <Box sx={{ textAlign: 'center' }}>
                  <ProfilePictureAvatar
                    filename={userData.publicPicture}
                    sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }}
                  />
                  <Typography variant="subtitle2">
                    Public: {userData.publicPicture}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* User Data Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1 }} />
                User Information
              </Typography>
              
              <Grid container spacing={2}>
                {/* Basic Info */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    value={userData.displayName}
                    onChange={(e) => setUserData(prev => ({ ...prev, displayName: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={userData.username}
                    onChange={(e) => setUserData(prev => ({ ...prev, username: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={userData.email}
                    onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                
                {/* Identity Options */}
                {identityOptions && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          value={userData.gender}
                          onChange={(e) => setUserData(prev => ({ ...prev, gender: e.target.value }))}
                        >
                          {identityOptions.gender.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Race</InputLabel>
                        <Select
                          value={userData.race}
                          onChange={(e) => setUserData(prev => ({ ...prev, race: e.target.value }))}
                        >
                          {identityOptions.race.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel>Sexual Orientation</InputLabel>
                        <Select
                          value={userData.sexualOrientation}
                          onChange={(e) => setUserData(prev => ({ ...prev, sexualOrientation: e.target.value }))}
                        >
                          {identityOptions.sexualOrientation.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Age"
                        value={userData.age}
                        onChange={(e) => {
                          const value = e.target.value;
                          
                          if (value === '') {
                            // Allow empty field for typing
                            setUserData(prev => ({ ...prev, age: '' as any }));
                          } else {
                            const numValue = parseInt(value);
                            if (!isNaN(numValue)) {
                              // Store the raw value during typing, don't apply constraints yet
                              setUserData(prev => ({ 
                                ...prev, 
                                age: numValue
                              }));
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value === '' || isNaN(parseInt(value))) {
                            // If field is empty or invalid on blur, set to default
                            setUserData(prev => ({ ...prev, age: 18 }));
                          } else {
                            // Apply constraints only when user finishes typing
                            const numValue = parseInt(value);
                            const constrainedAge = Math.max(13, Math.min(120, numValue));
                            setUserData(prev => ({ ...prev, age: constrainedAge }));
                          }
                        }}
                        inputProps={{ min: 13, max: 120 }}
                      />
                    </Grid>
                  </>
                )}
                
                {/* Neighborhood */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={[...neighborhoods].sort((a, b) => a.name.localeCompare(b.name))}
                    getOptionLabel={(option) => option.name}
                    value={neighborhoods.find(n => n._id === userData.homeNeighborhood) || null}
                    onChange={(_, newValue) => {
                      setUserData(prev => ({ 
                        ...prev, 
                        homeNeighborhood: newValue?._id || '' 
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Home Neighborhood"
                        placeholder="Search NYC neighborhoods..."
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(option =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Tags Section */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TagIcon sx={{ mr: 1 }} />
                Tags ({selectedTags.length}/{tagCount})
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <TextField
                  type="number"
                  label="Number of Tags"
                  value={tagCount}
                  onChange={(e) => setTagCount(Math.max(1, parseInt(e.target.value) || 15))}
                  sx={{ mr: 2, width: 150 }}
                />
                <Button variant="outlined" onClick={generateRandomTags}>
                  Generate Random Tags
                </Button>
              </Box>
              
              <Autocomplete
                multiple
                options={[...availableTags].sort((a, b) => a.localeCompare(b))}
                value={selectedTags}
                onChange={(_, newValue) => {
                  if (newValue.length <= tagCount) {
                    setSelectedTags(newValue);
                    setUserData(prev => ({ ...prev, tags: newValue }));
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tags"
                    placeholder="Search and select tags..."
                    helperText={`${selectedTags.length}/${tagCount} tags selected`}
                  />
                )}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </CardContent>
          </Card>
          
          {/* Bio Section */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CreateIcon sx={{ mr: 1 }} />
                Bio
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Bio Template</InputLabel>
                <Select
                  value={selectedBioTemplate}
                  onChange={(e) => setSelectedBioTemplate(e.target.value as number)}
                >
                  {BIO_TEMPLATES.map((template, index) => (
                    <MenuItem key={index} value={index}>
                      Template {index + 1}: {template.substring(0, 50)}...
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2">
                  Preview: {generateBio() || 'Select tags to preview bio'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
          
          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSkipUser}
              disabled={isCreating}
            >
              Skip This User
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={handleNextPicture}
              disabled={isCreating}
              startIcon={<PhotoIcon />}
            >
              Next Picture
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateUser}
              disabled={isCreating || !userData.displayName || !userData.username || !userData.gender}
              startIcon={isCreating ? <CircularProgress size={20} /> : <PersonIcon />}
            >
              {isCreating ? 'Creating...' : 'Create User'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MockUserCreator; 